/*
 * DELIM Sleep AI chat, used by blocks/delim_ai_chat.liquid.
 *
 * The reply logic is plain functions (exported for the unit tests in
 * tests/delim-ai-chat.test.js). The <delim-ai-chat> element is only
 * defined when this runs in a browser.
 */
(function () {
  var MOON =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z"/></svg>';

  /* ------------------------------------------------------------------
     Scripted sleep assistant (no API yet).

     TOPICS is the knowledge base. Each topic has:
       match     regex sources; each hit adds to the topic's score
       say       2+ wordings (arrays of paragraphs), rotated so repeats vary
       tips      bullet list shown under the answer
       more      deeper answer for "tell me more" / "how?" / "yes"
       products  keywords matched against product title, type and tags
     A product can also be linked without code: give it the Shopify tag
     "ai:<topic id>" (e.g. ai:snoring) to show it for that topic, or
     "ai:<words>" (e.g. ai:bad breath) to show it when someone says them.
       followups suggestion chips
     Safety topics (priority: true) always win over everything else.
     Facts here come from the store's own FAQ and product copy.
     ------------------------------------------------------------------ */
  var SAFETY = [
    {
      id: 'crisis',
      match: [
        'suicid',
        'kill myself',
        '(harm|hurt|cut) myself',
        'self.?harm',
        'end my life',
        'end it all',
        'want(ed)? to die',
        'wanna die',
        'better off dead',
        'no reason to live',
        'not worth living',
        "don'?t want to (live|be alive|be here any ?more|exist)",
        "don'?t want to wake up( again| any ?more)?[.!]*$",
        "(hope|wish) i (don'?t|never|wouldn'?t) wake up"
      ],
      say: [[
        "I'm really sorry you're feeling like this. You don't have to deal with it alone.",
        'If you are in danger right now, please call 999. You can talk to Samaritans any time on 116 123 — it is free and open 24/7.'
      ]]
    },
    {
      id: 'emergency',
      match: ['chest pain', "can'?t breathe(?! through)", 'cannot breathe(?! through)', 'struggling to breathe'],
      say: [[
        'If you have chest pain or are struggling to breathe right now, please call 999.',
        'For urgent advice that is not an emergency, call NHS 111.'
      ]]
    },
    {
      id: 'apnoea',
      match: ['apnoea', 'apnea', 'stops? breathing', 'stopped breathing', 'gasp', 'chok', 'cpap'],
      say: [[
        'Thanks for telling me. Pauses in breathing, gasping or choking at night can be signs of sleep apnoea, and that needs a proper check.',
        "Please speak to your GP. Mouth tape is not a treatment for sleep apnoea, and you shouldn't use it if you have it or think you might."
      ]],
      followups: ['Help me build a wind-down routine']
    },
    {
      id: 'blocked',
      match: ['blocked nose', 'stuffy', 'congest', '\\b(a|have a|got a) cold\\b', '\\bflu\\b', 'hay ?fever', "can'?t breathe through my nose", 'cannot breathe through my nose', 'deviated', 'sinus', 'polyps'],
      say: [[
        "If your nose is blocked, please don't use mouth tape tonight. You need to be able to breathe easily through your nose.",
        'Wait until it clears. If your nose is blocked most of the time, it is worth asking your GP or pharmacist why.'
      ]],
      followups: ['Help me build a wind-down routine', 'Is mouth tape safe for me?']
    },
    {
      id: 'child',
      // Only when a child is the sleeper: "my kids say I snore" or "a 35 year old man" must not match.
      match: [
        "\\b(my|our|your) (little |young |teenage )?(son|daughter|child|kid|baby|toddler|teen|teenager)s?\\b(?! (say|says|said|think|thinks|told|tell|tells|notice|noticed|complain|complains|hear|hears|heard|laugh|laughs)\\b)",
        "\\b(children|kids|toddlers|babies)\\b(?! (say|said|think|told|tell|notice|noticed|complain|hear|heard|laugh)\\b)",
        '\\bfor (a |my |our |your )?(child|kid|baby|toddler)\\b',
        '\\b(can|should|do|does) (a |my |our |your )?(child|kid|baby|toddler)\\b',
        '\\b([1-9]|1[0-5])[ -]?(year|yr)s?[ -]?old\\b'
      ],
      say: [[
        'Mouth tape is not designed for children.',
        'If a child snores most nights, breathes through their mouth or seems tired in the day, it is best to speak to your GP — it can be linked to things like enlarged tonsils.'
      ]],
      followups: ['Help me build a wind-down routine']
    },
    {
      id: 'medical',
      match: ['pregnan', 'medication', 'heart (condition|disease|problem|failure|issue)', 'copd', 'asthma', 'epilep', 'health condition', 'medical condition'],
      say: [[
        "With a health condition, or during pregnancy, it is best to check with your GP or midwife before trying mouth tape.",
        "I'm happy to share general sleep tips in the meantime."
      ]],
      followups: ['Help me build a wind-down routine', "I can't fall asleep before 1am"]
    }
  ];

  var TOPICS = [
    {
      id: 'about',
      label: 'what I can do',
      match: ['who are you', 'what are you', 'are you (a )?(bot|robot|ai|real|human|person)', 'chat ?gpt', 'is this (a )?(bot|ai)'],
      say: [[
        "I'm DELIM's sleep assistant — an automated helper, not a person or a doctor.",
        'I can help with snoring, dry mouth, falling asleep, night waking, routines and choosing products. For anything else, our UK care team is happy to help.'
      ]],
      link: 'contact',
      followups: ['I snore every night. What can help?', 'Help me build a wind-down routine']
    },
    {
      id: 'partner',
      label: 'your partner',
      absorbs: ['snoring'],
      match: ['partner', 'husband', '\\bwife\\b', 'boyfriend', 'girlfriend', 'other half', "(partner|husband|wife|boyfriend|girlfriend|other half)('s)? (snor|keeps me)"],
      say: [
        [
          'Sharing a bed with a snorer is hard on both of you.',
          'Most snoring comes from breathing through the mouth at night. Helping them breathe through their nose is often the simplest fix.'
        ],
        [
          "That sounds tiring — for both of you.",
          'Mouth breathing is behind a lot of snoring, so gentle nasal breathing at night is a good first step.'
        ]
      ],
      tips: ['Encourage sleeping on their side.', 'Earplugs can help you in the meantime.', 'If they gasp or stop breathing in their sleep, they should see a GP.'],
      more: ['Many DELIM customers say their partner noticed quieter nights within the first week of nasal breathing.'],
      products: ['tape', 'snor'],
      followups: ['Is mouth tape safe for me?', 'How do I use mouth tape?']
    },
    {
      id: 'snoring',
      label: 'snoring',
      ask: 'Tell me more about snoring',
      match: ['snor', 'loud breathing', 'noisy sleep'],
      say: [
        [
          'Snoring usually comes from the soft tissue at the back of your throat vibrating — and it is much more common when you breathe through your mouth at night.',
          'Helping yourself breathe through your nose is often the simplest place to start.'
        ],
        [
          'Most snoring happens when you breathe through your mouth while you sleep. The air makes the soft palate at the back of your throat vibrate.',
          'Keeping your lips gently closed, so you breathe through your nose, can make nights a lot quieter.'
        ]
      ],
      tips: ['Sleep on your side rather than your back.', 'Clear your nose before bed.', 'Go easy on alcohol in the evening — it relaxes the throat.'],
      more: [
        'Many DELIM customers notice quieter nights within the first week of nasal breathing.',
        'One important note: loud snoring with pauses, gasping or choking can be a sign of sleep apnoea. Mouth tape is not a treatment for that, so speak to your GP if it sounds familiar.'
      ],
      products: ['tape', 'snor', 'nasal'],
      followups: ['Is mouth tape safe for me?', 'How do I use mouth tape?']
    },
    {
      id: 'drymouth',
      label: 'the dry mouth',
      ask: 'Why do I wake up with a dry mouth?',
      match: ['dry mouth', 'dry throat', 'mouth (is |feels )?dry', 'morning breath', 'bad breath', 'parched', 'thirsty', 'mouth breath', 'mouth (falls |hangs |is |stays )?open', 'open mouth', 'breath(e|ing) through (my|the|their) mouth'],
      say: [
        [
          'Waking up with a dry mouth is a classic sign you have been breathing through your mouth for part of the night.',
          'Your nose warms and moistens the air you breathe, so nasal breathing keeps your mouth moist and mornings feel fresher.'
        ],
        [
          'A dry mouth in the morning usually means your mouth fell open while you slept.',
          'Breathing through your nose instead keeps saliva where it should be — and helps with morning breath too.'
        ]
      ],
      tips: ['Keep a glass of water by the bed.', 'Avoid alcohol close to bedtime.', 'Try keeping your lips gently closed overnight.'],
      more: [
        'Mouth breathing dries out your mouth overnight, which is also why it is linked to morning breath.',
        'A lot of people find a week of nasal breathing is enough to notice the difference.'
      ],
      products: ['tape', 'dry'],
      followups: ['How do I use mouth tape?', 'I also snore']
    },
    {
      id: 'safety',
      label: 'safety',
      ask: 'Is mouth tape safe for me?',
      match: ['\\bsafe', 'side effect', 'dangerous', '\\brisk', 'suffocat', 'can i still breathe', 'scared', 'nervous'],
      say: [
        [
          'DELIM mouth tape is hypoallergenic and made with breathable fabric, so air can still get through in an emergency. It peels away the moment you open your mouth, so you stay in control all night.',
          'It is designed for healthy adults who can breathe comfortably through their nose.'
        ]
      ],
      tips: ["Don't use it if your nose is blocked or you have a cold.", "Don't use it if you have, or think you have, sleep apnoea.", 'Not for children.', 'If you have a health condition, check with your GP first.'],
      more: ['If you feel nervous, try wearing a strip for 10 minutes while you read or watch TV. Most people relax once they feel how gentle it is.'],
      products: ['tape'],
      followups: ['How do I use mouth tape?', 'Will it hurt to remove?']
    },
    {
      id: 'howto',
      label: 'how to use it',
      ask: 'How do I use mouth tape?',
      match: ['how (do|should|can) i (use|start|wear|put)', 'how to (use|wear|apply)', 'how does (it|mouth tape) work', 'put it on', '\\bapply', 'first night', 'first week', 'instructions', 'get started'],
      say: [['It takes about ten seconds:'], ['Here is how to use it:']],
      tips: ['Wipe your lips so they are clean and dry — no lip balm.', 'Place one strip horizontally across your lips.', 'Breathe through your nose and sleep as normal.', 'Peel off gently in the morning.'],
      more: ["In your first week, start easy. Some people wear it for the first hour or two, then build up to a full night. There's no wrong way."],
      products: ['tape'],
      followups: ['Will it hurt to remove?', 'Does it work with a beard?']
    },
    {
      id: 'beard',
      label: 'facial hair',
      match: ['beard', 'stubble', 'facial hair', 'moustache', 'mustache'],
      say: [[
        'Yes — DELIM strips are designed to hold on stubble and facial hair, and still peel off cleanly in the morning.',
        'With a thick moustache, the strip holds best when the hair right above the lip is kept short.'
      ]],
      products: ['tape'],
      followups: ['How do I use mouth tape?', 'Will it hurt to remove?']
    },
    {
      id: 'skin',
      label: 'your skin',
      match: ['sensitive', '\\brash', 'allerg', 'irritat', '\\bhurts?\\b', 'painful', '\\bremov', 'residue', 'eczema', 'red (skin|lips)'],
      say: [[
        'DELIM uses a hypoallergenic, dermatologist-tested adhesive, and it is designed to come off easily and pain-free.',
        'If your skin is very sensitive, try a strip for an hour while you are awake first and see how it feels.'
      ]],
      tips: ['Apply to clean, dry skin.', 'Peel off slowly in the morning.', 'Stop using it if you notice any redness.'],
      products: ['tape'],
      followups: ['How do I use mouth tape?', 'What is your returns policy?']
    },
    {
      id: 'nightwake',
      label: 'waking in the night',
      ask: 'I keep waking up at night',
      match: ['wak(e|ing)( up)? (in|during) the night', 'keep waking', 'wake (up )?at [0-9]', 'middle of the night', 'night waking', 'need the (loo|toilet)'],
      say: [[
        'Waking in the night is very common. The usual causes are light, noise, temperature, alcohol, or needing the loo.'
      ]],
      tips: ['Keep the room cool, dark and quiet.', 'Avoid alcohol close to bedtime — it breaks up sleep later in the night.', 'Cut down on drinks in the last hour before bed.', "If you wake, don't check the time or your phone."],
      more: ['Mouth breathing can also wake you with a dry mouth or snoring. If you often wake up thirsty, nasal breathing may help.'],
      products: ['mask', 'earplug'],
      followups: ['I wake up with a dry mouth. Why?', 'Help me build a wind-down routine']
    },
    {
      id: 'fallasleep',
      label: 'falling asleep',
      ask: "I can't fall asleep",
      match: ['fall(ing)? asleep', 'get to sleep', "can'?t sleep", 'cannot sleep', 'insomnia', 'lie awake', 'lying awake', 'awake for hours', 'before [0-9]', 'too late'],
      say: [
        [
          'Trouble falling asleep is usually about light, timing and a busy mind.',
          'Your body clock responds strongly to light, so the hour before bed matters a lot.'
        ],
        [
          'Lying awake is frustrating. The good news is that small changes to light and timing often help within a week or two.'
        ]
      ],
      tips: ['Get daylight within an hour of waking.', 'Keep the same wake-up time, even at weekends.', 'No caffeine after 2pm.', 'Dim the lights and put screens away 30–60 minutes before bed.'],
      more: ['If you have been lying awake for more than 20 minutes, get up, do something calm in low light, and go back when you feel sleepy. It stops your bed becoming a place for worrying.'],
      products: ['mask', 'mist', 'spray', 'routine'],
      followups: ['Help me build a wind-down routine', 'I keep waking up at night']
    },
    {
      id: 'tired',
      label: 'feeling tired',
      match: ['tired', 'exhausted', 'groggy', 'no energy', 'fatigue', 'sleepy (in|during) the day', 'unrefreshed', 'not rested'],
      say: [[
        'Waking up tired, even after enough hours, often means your sleep is broken or shallow.',
        'Snoring, mouth breathing, alcohol and an irregular schedule are common causes.'
      ]],
      tips: ['Keep the same bedtime and wake-up time every day.', 'Get outside in daylight in the morning.', 'Notice if you wake with a dry mouth — a sign of mouth breathing.'],
      more: ['If you feel very sleepy during the day, or someone has noticed you stop breathing in your sleep, please speak to your GP. Those can be signs of sleep apnoea.'],
      products: ['tape'],
      followups: ['I wake up with a dry mouth. Why?', 'I snore every night. What can help?']
    },
    {
      id: 'routine',
      label: 'a routine',
      ask: 'Help me build a wind-down routine',
      match: ['routine', 'wind.?down', 'before bed', 'bedtime', '\\brelax', 'sleep hygiene', '\\bquiz', '\\bhabit'],
      say: [
        ["A short, steady routine tells your body it's time to sleep. Here is a simple 30-minute plan:"],
        ['Here is an easy 30-minute wind-down you can start tonight:']
      ],
      tips: ['30 min before: dim the lights and put your phone on charge outside the bedroom.', '20 min before: something calm — reading, stretching or a warm shower.', '10 min before: make sure the room is cool and dark.', 'In bed: slow breaths through your nose.'],
      more: ['Consistency matters more than any single step. Try the same routine for a week and see how you feel.'],
      products: ['mask', 'mist', 'spray', 'routine'],
      followups: ["I can't fall asleep before 1am", 'I keep waking up at night']
    },
    {
      id: 'stress',
      label: 'stress',
      match: ['stress', 'anxi', 'worr', 'racing', 'overthink', "mind won'?t", 'switch off'],
      say: [[
        'A busy mind is one of the most common reasons people struggle to switch off.',
        'It helps to give your thoughts somewhere to go before bed.'
      ]],
      tips: ["Write tomorrow's to-do list before you go to bed.", 'Try slow nasal breathing: in for 4, out for 6, for a few minutes.', 'Keep your phone out of reach.'],
      more: ['If worry is affecting your sleep most nights, it is worth talking to your GP. Talking therapy for sleep (CBT-I) works well for many people.'],
      products: ['mist', 'routine'],
      followups: ['Help me build a wind-down routine', "I can't fall asleep before 1am"]
    },
    {
      id: 'drinks',
      label: 'caffeine and alcohol',
      match: ['coffee', 'caffeine', '\\btea\\b', 'alcohol', '\\bwine\\b', '\\bbeer\\b', 'energy drink'],
      say: [[
        'Both can quietly hurt your sleep.',
        'Caffeine stays in your body for hours. Alcohol may help you nod off, but it breaks up your sleep later in the night and makes snoring worse.'
      ]],
      tips: ['No caffeine after 2pm.', 'Leave about 3 hours between your last alcoholic drink and bed.', 'Swap your evening cup for a caffeine-free one.'],
      followups: ['Help me build a wind-down routine', 'I keep waking up at night']
    },
    {
      id: 'delivery',
      label: 'delivery',
      ask: 'How fast is delivery?',
      match: ['deliver', 'shipping', '\\bship', '\\bpost(age|ed)?\\b', 'arrive', 'royal mail', 'dispatch', 'next day'],
      say: [[
        'Delivery is free on every UK order, sent Royal Mail tracked from our studio in Reading.',
        'Orders placed before 2pm Monday to Friday leave the same day, and most UK postcodes get them the next working day. The Highlands and Islands usually take one extra day.'
      ]],
      followups: ['What is your returns policy?', "What's the best starter bundle?"]
    },
    {
      id: 'returns',
      label: 'returns',
      ask: 'What is your returns policy?',
      match: ['\\breturn(s|ed)?\\b', 'refund', 'money.?back', 'guarantee', "(doesn'?t|does not|didn'?t) work", '\\bcancel'],
      say: [[
        "You're covered by our 30-night money-back guarantee.",
        "If it doesn't work for you, post it back freepost and we'll refund you — even if the pack is empty."
      ]],
      link: 'contact',
      followups: ['How fast is delivery?', 'How do I use mouth tape?']
    },
    {
      id: 'price',
      label: 'price',
      match: ['price', '\\bcost', 'how much', 'expensive', 'cheap', 'how long does (a|one|the) pack', 'how many strips', 'per night'],
      say: function (products) {
        var tape = pickProducts(products, ['tape'], 1)[0];
        var lines = ['Each pack has 30 strips — a full month of nightly use.'];
        if (tape) lines.unshift(tape.title + ' is ' + tape.price + '.');
        lines.push('Delivery is free across the UK, and you have 30 nights to decide if it works for you.');
        return lines;
      },
      products: ['tape'],
      followups: ['How fast is delivery?', 'Is mouth tape safe for me?']
    },
    {
      id: 'compare',
      label: 'other brands',
      match: ['hostage', 'other brand', 'better than', 'difference', 'amazon', 'compare', '\\bvs\\b', 'versus'],
      say: [[
        'DELIM is designed and posted from Reading, so there are no customs fees or long waits.',
        'It uses a hypoallergenic adhesive that holds on stubble and peels off cleanly, and it comes with a 30-night guarantee and freepost returns.'
      ]],
      products: ['tape'],
      followups: ['Is mouth tape safe for me?', 'What is your returns policy?']
    },
    {
      id: 'brand',
      label: 'DELIM',
      match: ['who makes', 'where are you (based|from)', 'founder', 'about (you|delim)', 'your company'],
      say: [[
        'DELIM is a small UK sleep brand, founded by Amirhossein and Asal Delkhosh and based in Reading.',
        'We started with mouth tape made for British sleepers, and we are building a wider range for better nights.'
      ]],
      followups: ["What's the best starter bundle?", 'How fast is delivery?']
    },
    {
      id: 'contact',
      label: 'our team',
      match: ['\\bhuman', 'real person', 'speak to', 'talk to (someone|a person)', 'contact', '\\bemail', 'customer service', 'customer support', 'complain', 'my order', 'track (my )?(order|parcel)'],
      say: [['Our UK-based care team reads every message and usually replies within 2 hours on weekdays (Mon–Fri, 9am–5pm).']],
      link: 'contact',
      followups: ['How fast is delivery?', 'What is your returns policy?']
    },
    {
      id: 'checkout',
      label: 'checking out',
      match: ['check ?out', '\\bbasket', '\\bcart\\b', '\\bpay(ment)?\\b', 'place (an |my )?order', 'how (do|can) i (order|buy)'],
      say: [[
        'Tap Add on any product to put it in your basket, then open your basket to check out.',
        'Delivery is free across the UK, and you have 30 nights to decide if it works for you.'
      ]],
      link: 'cart',
      products: ['tape'],
      followups: ['How fast is delivery?', 'What is your returns policy?']
    },
    {
      id: 'shop',
      label: 'products',
      ask: "What's the best starter bundle?",
      match: ['bundle', 'starter', 'recommend', 'what should i (buy|get|try)', 'which product', 'best product', '\\bbuy', '\\bshop', '\\bgift', 'product'],
      say: function (products) {
        if (products.length === 1) {
          return [
            'Right now our range starts with ' + products[0].title + ' (' + products[0].price + ').',
            'It is the best first step if you snore or wake up with a dry mouth.'
          ];
        }
        return ["If you're new to DELIM, start with breathing — it makes the biggest difference for most people. Here is where I'd begin:"];
      },
      allProducts: true,
      followups: ['Is mouth tape safe for me?', 'How do I use mouth tape?']
    }
  ];

  var GREETING = /^(hi|hello|hey|hiya|good (morning|afternoon|evening))\b/;
  var THANKS = /\b(thanks|thank you|cheers|ta)\b/;
  // "Tell me more" style replies: every word must be one of these, so "how do I check out" is not one.
  var MORE_WORDS = ['yes', 'yeah', 'yep', 'ok', 'okay', 'sure', 'please', 'go', 'on', 'tell', 'me', 'more', 'how', 'so', 'why', 'really', 'and', 'what', 'else'];
  function isMore(t) {
    var words = t.replace(/[^a-z' ]/g, ' ').split(/\s+/).filter(Boolean);
    return words.length > 0 && words.every(function (w) { return MORE_WORDS.indexOf(w) > -1; });
  }
  // Words in product titles too general to identify a product ("I can't sleep" must not find "Sleep Mask").
  var TITLE_STOP = ['delim', 'sleep', 'mouth', 'pack', 'night', 'nights', 'with', 'for', 'and', 'the', 'set', 'kit', 'bundle'];
  var BUY = /\b(add|buy|basket|cart|purchase)\b|i'?ll take|\bi(')?d like (some|the|a|an)\b|\bi want (some|the|a|an|one|two|[0-9])\b/;

  // Products named by a distinctive title word ("tape", "masks").
  function productsByWord(products, t) {
    return products.filter(function (p) {
      return p.title.toLowerCase().split(/[^a-z]+/).some(function (w) {
        return w.length > 3 && TITLE_STOP.indexOf(w) === -1 && new RegExp('\\b' + w + 's?\\b').test(t);
      });
    });
  }

  function pickProducts(products, keywords, limit) {
    var picked = [];
    (keywords || []).forEach(function (k) {
      products.forEach(function (p) {
        if (picked.length < limit && picked.indexOf(p) === -1 && p.text.indexOf(k) > -1) picked.push(p);
      });
    });
    return picked;
  }

  // Lower-case phrases from the product's "ai:" tags (see the top of this file).
  function aiTags(p) {
    return Array.isArray(p.ai) ? p.ai : [];
  }

  // Products tagged "ai:<topic id>" first, then the topic's keyword matches.
  function topicProducts(products, topic, limit) {
    var picked = products.filter(function (p) { return aiTags(p).indexOf(topic.id) > -1; }).slice(0, limit);
    pickProducts(products, topic.products, limit).forEach(function (p) {
      if (picked.length < limit && picked.indexOf(p) === -1) picked.push(p);
    });
    return picked;
  }

  // Products whose "ai:" tag words appear in the message ("bad breath").
  function productsByTag(products, t) {
    return products.filter(function (p) {
      return aiTags(p).some(function (tag) {
        return tag.length > 2 && new RegExp('\\b' + tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + 's?\\b').test(t);
      });
    });
  }

  function hits(topic, t) {
    var score = 0;
    topic.match.forEach(function (src) {
      if (new RegExp(src, 'i').test(t)) score += src.length > 8 ? 2 : 1;
    });
    return score;
  }

  function pickVariant(topic, ctx, products) {
    if (typeof topic.say === 'function') return topic.say(products);
    var i = ctx.used[topic.id] == null ? Math.floor(Math.random() * topic.say.length) : (ctx.used[topic.id] + 1) % topic.say.length;
    ctx.used[topic.id] = i;
    return topic.say[i].slice();
  }

  function one(list) {
    return list[Math.floor(Math.random() * list.length)];
  }

  function topicReply(topic, ctx, products) {
    var recs = topic.allProducts ? products.slice(0, 3) : topicProducts(products, topic, 3);
    return {
      paragraphs: pickVariant(topic, ctx, products),
      tips: topic.tips || [],
      products: recs,
      followups: (topic.followups || []).slice(),
      link: topic.link || null
    };
  }

  /**
   * Free scripted reply. Returns
   *   { kind, paragraphs: string[], tips: string[], products: object[],
   *     followups: string[], link: 'contact' | 'signin' | 'cart' | null }
   * kind: safety | smalltalk | product | more | topic | fallback
   * ("fallback" = the script couldn't answer; that's when the AI is asked).
   */
  function scriptedReply(message, products, ctx) {
    var t = message.toLowerCase().replace(/[’‘]/g, "'").trim();
    var words = t.split(/\s+/).length;
    var reply = null;

    // 1. Safety always comes first.
    for (var s = 0; s < SAFETY.length && !reply; s++) {
      if (hits(SAFETY[s], t)) {
        reply = topicReply(SAFETY[s], ctx, []);
        reply.kind = 'safety';
        ctx.last = null;
      }
    }

    // 2. Small talk.
    if (!reply && words <= 4 && GREETING.test(t)) {
      reply = {
        kind: 'smalltalk',
        paragraphs: [one(['Hi! How have you been sleeping lately?', 'Hello! Tell me a little about your sleep — what would you like to improve?'])],
        followups: ['I snore every night. What can help?', "I can't fall asleep before 1am", 'I wake up tired']
      };
    }
    if (!reply && words <= 6 && THANKS.test(t)) {
      reply = {
        kind: 'smalltalk',
        paragraphs: [one(["You're welcome. Sleep well tonight!", 'Happy to help. Is there anything else about your sleep I can help with?'])],
        followups: ['Help me build a wind-down routine', "What's the best starter bundle?"]
      };
    }

    // 3. A product by name ("Tell me about …").
    if (!reply) {
      var product = products.filter(function (p) {
        return p.title.length > 3 && t.indexOf(p.title.toLowerCase()) > -1;
      })[0];
      if (product) {
        var tape = /mouth.?tape/.test(product.text || product.title.toLowerCase());
        reply = {
          kind: 'product',
          paragraphs: [product.title + ' costs ' + product.price + '.', product.desc || ''].filter(Boolean),
          products: [product],
          followups: tape
            ? ['Is mouth tape safe for me?', 'How do I use mouth tape?']
            : ['Help me build a wind-down routine', "What's the best starter bundle?"]
        };
      }
    }

    // 4. Short follow-up ("yes", "how?", "tell me more") goes deeper on the last topic.
    var anyTopic = TOPICS.some(function (topic) { return hits(topic, t) > 0; });
    if (!reply && !anyTopic && ctx.last && words <= 5 && isMore(t)) {
      if (ctx.last.more && !ctx.moreGiven) {
        reply = {
          kind: 'more',
          paragraphs: ctx.last.more.slice(),
          products: topicProducts(products, ctx.last, 2),
          followups: (ctx.last.followups || []).slice()
        };
        ctx.moreGiven = true;
      } else {
        reply = {
          kind: 'more',
          paragraphs: ['That is the main thing I would focus on. Is there anything else about your sleep you would like help with?'],
          followups: ['Help me build a wind-down routine', "What's the best starter bundle?"]
        };
      }
    }

    // 5. A product by a word in its name ("tape", "add the mask") or its "ai:" tags:
    //    when there's no topic, or they want to buy.
    if (!reply) {
      var named = productsByWord(products, t);
      productsByTag(products, t).forEach(function (p) {
        if (named.indexOf(p) === -1) named.push(p);
      });
      if (named.length && (!anyTopic || BUY.test(t))) {
        var buying = BUY.test(t);
        reply = {
          kind: 'product',
          paragraphs: buying
            ? [one(['Here you go. Tap Add to put it in your basket.', 'Here it is. Tap Add and it goes straight into your basket.'])]
            : named.length === 1
              ? [named[0].title + ' costs ' + named[0].price + '.', named[0].desc || ''].filter(Boolean)
              : ['Here is what I found:'],
          products: named.slice(0, 3),
          link: buying ? 'cart' : null,
          followups: named.some(function (p) { return /mouth.?tape/.test(p.text || p.title.toLowerCase()); })
            ? ['How do I use mouth tape?', 'How fast is delivery?']
            : ["What's the best starter bundle?", 'How fast is delivery?']
        };
      }
    }

    // 6. Best matching topics.
    if (!reply) {
      var ranked = TOPICS.map(function (topic) { return { topic: topic, score: hits(topic, t) }; })
        .filter(function (r) { return r.score > 0; })
        .sort(function (a, b) { return b.score - a.score; });

      if (ranked.length) {
        var main = ranked[0].topic;
        reply = topicReply(main, ctx, products);
        reply.kind = 'topic';
        // Products whose "ai:" tag words were said go first.
        var tagged = productsByTag(products, t);
        if (tagged.length) {
          reply.products = tagged.concat(reply.products.filter(function (p) { return tagged.indexOf(p) === -1; })).slice(0, 3);
        }
        ctx.last = main;
        ctx.moreGiven = false;

        var second = ranked.filter(function (r) {
          return r.topic !== main && (main.absorbs || []).indexOf(r.topic.id) === -1 && r.topic.ask;
        })[0];
        if (second) {
          reply.paragraphs.push(
            one(['You also mentioned ' + second.topic.label + ' — I can cover that next.', 'I can help with ' + second.topic.label + ' too when you are ready.'])
          );
          reply.followups = reply.followups.filter(function (f) { return !hits(second.topic, f.toLowerCase()); });
          reply.followups.unshift(second.topic.ask);
        }
      }
    }

    // 7. Didn't understand.
    if (!reply) {
      ctx.misses = (ctx.misses || 0) + 1;
      reply =
        ctx.misses >= 2
          ? {
              paragraphs: ["That one is outside what I can answer well. Our UK care team can help — they usually reply within 2 hours on weekdays."],
              link: 'contact',
              followups: ['I snore every night. What can help?', 'How fast is delivery?']
            }
          : {
              paragraphs: [
                one([
                  'I want to make sure I give you a useful answer. Could you tell me a bit more — for example, do you snore, wake up tired, or find it hard to fall asleep?',
                  "I'm not sure I've understood that one. I can help with snoring, dry mouth, falling asleep, night waking, routines, delivery and returns."
                ])
              ],
              followups: ['I snore every night. What can help?', 'I wake up tired', "I can't fall asleep before 1am"]
            };
      reply.kind = 'fallback';
    } else {
      ctx.misses = 0;
    }

    // Don't suggest the exact question that was just asked.
    reply.followups = (reply.followups || []).filter(function (f) {
      return f.toLowerCase() !== t;
    }).slice(0, 3);

    return reply;
  }

  // "Thinking" time grows a little with answer length, like a real model.
  function withDelay(reply) {
    var length = (reply.paragraphs || []).join(' ').length;
    var delay = Math.min(500 + length * 2, 1600) + Math.random() * 300;
    return new Promise(function (resolve) {
      setTimeout(function () { resolve(reply); }, delay);
    });
  }

  // Guest asked something the script can't answer: invite them to sign in.
  function guestNudge(local) {
    return {
      kind: 'nudge',
      paragraphs: [
        "That's a bit beyond what the free Sleep Assistant can answer.",
        "Sign in and DELIM Sleep AI can help with more detailed questions. It's free — you're never charged for using it."
      ],
      link: 'signin',
      followups: local.followups
    };
  }

  function cleanList(list, max, maxLength) {
    return (Array.isArray(list) ? list : [])
      .filter(function (x) { return typeof x === 'string' && x.trim(); })
      .slice(0, max)
      .map(function (x) { return x.trim().slice(0, maxLength); });
  }

  // Turns the AI server's JSON into the same shape as a scripted reply.
  function normalizeAi(data, products) {
    data = data || {};
    var paragraphs = cleanList(data.paragraphs, 8, 1200);
    if (!paragraphs.length && typeof data.text === 'string') paragraphs = cleanList(data.text.split(/\n{2,}/), 8, 1200);
    if (!paragraphs.length) throw new Error('empty reply');
    var recs = cleanList(data.products, 3, 200)
      .map(function (h) {
        return products.filter(function (p) { return p.handle === h || p.title === h; })[0];
      })
      .filter(Boolean);
    return {
      kind: 'ai',
      paragraphs: paragraphs,
      tips: cleanList(data.tips, 6, 300),
      products: recs,
      followups: cleanList(data.followups, 3, 120),
      link: data.link === 'contact' ? 'contact' : null
    };
  }

  /**
   * Hybrid routing. Returns 'ai', 'nudge' or 'local'.
   * - safety answers are always scripted
   * - signed-in + AI live: AI answers what the script can't (or everything, in "always" mode)
   * - guests: scripted; if the script can't answer and AI is live, invite them to sign in
   */
  function chooseRoute(local, state) {
    var canUseAi = state.aiEnabled && state.signedIn && !state.aiPaused;
    var wantsAi = local.kind === 'fallback' || (state.aiAlways && local.kind !== 'safety');
    if (canUseAi && wantsAi) return 'ai';
    if (state.aiEnabled && !state.signedIn && local.kind === 'fallback') return 'nudge';
    return 'local';
  }

  /**
   * What to show when the AI server fails. `status` is the HTTP status
   * (undefined for network errors and timeouts). Returns
   * { reply, signedOut, paused } so the element can update its state.
   */
  function aiErrorReply(status, local) {
    if (status === 401 || status === 403) {
      return {
        signedOut: true,
        reply: {
          kind: 'nudge',
          paragraphs: ['Your session has ended. Please sign in again to keep using DELIM Sleep AI — it is still free.'],
          link: 'signin',
          followups: local.followups
        }
      };
    }
    if (status === 429) {
      var note = "You've used today's free DELIM Sleep AI messages. They reset tomorrow.";
      if (local.kind !== 'fallback') {
        return { paused: true, reply: Object.assign({}, local, { paragraphs: [note].concat(local.paragraphs) }) };
      }
      return {
        paused: true,
        reply: {
          kind: 'limit',
          paragraphs: [note, 'In the meantime I can still help with common questions about snoring, sleep routines, delivery and returns.'],
          followups: local.followups
        }
      };
    }
    // AI is down or slow: fall back to the script without making a fuss.
    if (local.kind !== 'fallback') return { reply: local };
    return {
      reply: {
        kind: 'unavailable',
        paragraphs: ["DELIM Sleep AI is taking a short break, so I couldn't answer that one.", 'Please try again in a little while, or contact our team.'],
        link: 'contact',
        followups: local.followups
      }
    };
  }

  /**
   * Body for POST /cart/add.js. `cartUi` is the theme's cart type ('drawer',
   * 'notification' or anything else for the cart page). We ask Shopify to send
   * back the HTML of the cart parts the theme needs to refresh, so the item
   * shows up in the cart straight away.
   */
  function cartAddRequest(variantId, cartUi, path) {
    var sections = ['cart-icon-bubble'];
    if (cartUi === 'drawer') sections = ['cart-drawer', 'cart-icon-bubble'];
    if (cartUi === 'notification') sections = ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
    return {
      id: Number(variantId),
      quantity: 1,
      sections: sections.join(','),
      sections_url: path || '/'
    };
  }

  /** Button label after a failed add. 422 = Shopify refused it (sold out or no more stock). */
  function cartAddErrorLabel(status) {
    return status === 422 ? 'Sold out' : 'Try again';
  }

  /**
   * Question passed in the page address, e.g. /?ask=Tell+me+about+X from the
   * "Ask DELIM Sleep AI" box on product pages. Returns '' when there is none.
   * Trimmed, spaces collapsed and capped at ASK_MAX characters, so a long link
   * can't send a huge message. It still goes through scriptedReply, so safety
   * answers win as usual.
   */
  var ASK_MAX = 300;
  function askFromQuery(search) {
    if (typeof search !== 'string' || !search) return '';
    var value;
    try {
      value = new URLSearchParams(search).get('ask');
    } catch {
      return '';
    }
    if (!value) return '';
    return value.replace(/\s+/g, ' ').trim().slice(0, ASK_MAX).trim();
  }

  /* ---------- Browser element ---------- */
  // Where to scroll so the chat's top sits just under the header, or null when
  // it's already there. chatTop is the chat's distance from the top of the page.
  // Scrolls both ways: up on the homepage, down to the chat on product pages.
  var CHAT_OFFSET = 80;
  function chatScrollTarget(scrollY, chatTop) {
    var top = Math.max(chatTop - CHAT_OFFSET, 0);
    return Math.abs(scrollY - top) <= 40 ? null : top;
  }

  function defineElement() {
    function el(tag, className, text) {
      var node = document.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    }

    class DelimAiChat extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.thread = this.querySelector('[data-ai-thread]');
        this.typing = this.querySelector('[data-ai-typing]');
        this.form = this.querySelector('[data-ai-form]');
        this.input = this.querySelector('[data-ai-input]');
        this.submit = this.querySelector('[data-ai-submit]');
        this.contactUrl = this.getAttribute('data-contact-url') || '/pages/contact';
        this.loginUrl = this.getAttribute('data-login-url') || '/account/login';
        this.cartUrl = this.getAttribute('data-cart-url') || '/cart';
        this.signedIn = this.getAttribute('data-signed-in') === 'true';
        this.aiEnabled = this.getAttribute('data-ai-live') === 'true';
        this.aiEndpoint = this.getAttribute('data-ai-endpoint') || '/apps/sleep-ai';
        this.aiAlways = this.getAttribute('data-ai-mode') === 'always';
        this.aiPaused = false;
        this.placeholder = this.input.getAttribute('placeholder');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.history = [];
        this.ctx = { used: {} };
        this.busy = false;
        this.run = 0;

        try {
          this.products = JSON.parse(this.querySelector('[data-ai-products]').textContent) || [];
        } catch {
          this.products = [];
        }

        this.input.addEventListener('input', this.onInput.bind(this));
        this.input.addEventListener('keydown', this.onKeydown.bind(this));
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.addEventListener('click', this.onClick.bind(this));

        // Arrived with ?ask= (an "Ask DELIM Sleep AI" link, or the buy box chip
        // without JS): ask it once, then drop ?ask= so a reload doesn't ask again.
        var asked = askFromQuery(window.location.search);
        if (asked) {
          try {
            var url = new URL(window.location.href);
            url.searchParams.delete('ask');
            window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
          } catch {}
          this.send(asked);
          this.scrollToChat();
        }
      }

      onInput() {
        this.input.style.height = 'auto';
        this.input.style.height = Math.min(this.input.scrollHeight, 200) + 'px';
        this.updateSend();
      }

      updateSend() {
        this.submit.disabled = this.busy || !this.input.value.trim();
      }

      onKeydown(e) {
        if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
          e.preventDefault();
          this.send(this.input.value);
        }
      }

      onSubmit(e) {
        e.preventDefault();
        this.send(this.input.value);
      }

      onClick(e) {
        var sendBtn = e.target.closest('[data-ai-send-text]');
        if (sendBtn) {
          this.send(sendBtn.getAttribute('data-ai-send-text'));
          this.scrollToChat();
          return;
        }
        var addBtn = e.target.closest('[data-ai-add]');
        if (addBtn) {
          this.addToCart(addBtn);
          return;
        }
        if (e.target.closest('[data-ai-new]')) this.reset();
      }

      scrollToChat() {
        var top = chatScrollTarget(window.pageYOffset, this.getBoundingClientRect().top + window.pageYOffset);
        if (top === null) return;
        window.scrollTo({ top: top, behavior: this.reduceMotion ? 'auto' : 'smooth' });
      }

      // Keep the composer in view while a reply grows, without hiding the reply's start.
      followReply(row) {
        var formBottom = this.form.getBoundingClientRect().bottom;
        var overflow = formBottom - window.innerHeight + 16;
        if (overflow <= 0) return;
        var room = row.getBoundingClientRect().top - 90;
        var by = Math.min(overflow, room);
        if (by > 0) window.scrollBy(0, by);
      }

      reset() {
        this.run++;
        this.history = [];
        this.ctx = { used: {} };
        this.busy = false;
        this.thread.innerHTML = '';
        this.thread.removeAttribute('aria-busy');
        this.typing.hidden = true;
        this.classList.remove('is-chatting');
        this.input.value = '';
        this.input.setAttribute('placeholder', this.placeholder);
        this.onInput();
        this.input.focus();
      }

      send(raw) {
        var text = (raw || '').trim();
        if (!text || this.busy) return;

        var run = this.run;
        var self = this;
        this.busy = true;
        this.classList.add('is-chatting');
        this.input.value = '';
        this.input.setAttribute('placeholder', this.input.getAttribute('data-placeholder-reply'));
        this.onInput();
        this.clearFollowups();

        this.thread.appendChild(el('div', 'ai-msg-user', text));
        this.history.push({ role: 'user', content: text });
        this.typing.hidden = false;

        this.getReply(text)
          .catch(function () {
            return { paragraphs: ['Sorry, something went wrong. Please try again.'] };
          })
          .then(function (reply) {
            if (run !== self.run) return;
            self.typing.hidden = true;
            self.history.push({ role: 'assistant', content: (reply.paragraphs || []).join('\n\n') });
            return self.showReply(reply, run);
          })
          .then(function () {
            if (run !== self.run) return;
            self.busy = false;
            self.updateSend();
          });
      }

      getReply(text) {
        var self = this;
        var local = scriptedReply(text, this.products, this.ctx);
        var route = chooseRoute(local, {
          aiEnabled: this.aiEnabled,
          signedIn: this.signedIn,
          aiPaused: this.aiPaused,
          aiAlways: this.aiAlways
        });

        if (route === 'ai') {
          return this.askAi(text).then(
            function (reply) {
              self.ctx.misses = 0;
              return reply;
            },
            function (err) {
              var result = aiErrorReply(err && err.status, local);
              if (result.signedOut) self.signedIn = false;
              if (result.paused) self.aiPaused = true;
              return withDelay(result.reply);
            }
          );
        }
        return withDelay(route === 'nudge' ? guestNudge(local) : local);
      }

      askAi(text) {
        var products = this.products;
        var controller = window.AbortController ? new AbortController() : null;
        var timer = setTimeout(function () { if (controller) controller.abort(); }, 25000);
        return fetch(this.aiEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            message: text.slice(0, 1000),
            // Last few turns only (the current message is sent separately).
            history: this.history.slice(-11, -1)
          }),
          signal: controller ? controller.signal : undefined
        })
          .then(function (res) {
            clearTimeout(timer);
            if (!res.ok) {
              var err = new Error('AI request failed');
              err.status = res.status;
              throw err;
            }
            return res.json();
          })
          .then(function (data) {
            return normalizeAi(data, products);
          });
      }

      clearFollowups() {
        this.thread.querySelectorAll('.ai-followups').forEach(function (n) { n.remove(); });
      }

      // Writes the answer word by word (like a streaming model), then adds tips, products and chips.
      showReply(reply, run) {
        var self = this;
        var row = el('div', 'ai-msg-bot');
        var avatar = el('span', 'ai-avatar');
        avatar.innerHTML = MOON;
        var body = el('div', 'ai-msg-body');
        row.appendChild(avatar);
        row.appendChild(body);
        this.thread.setAttribute('aria-busy', 'true');
        this.thread.appendChild(row);

        var paragraphs = (reply.paragraphs || []).map(function (text) {
          var p = el('p');
          body.appendChild(p);
          return { node: p, words: text.split(' ') };
        });

        var streamed = new Promise(function (resolve) {
          if (self.reduceMotion) {
            paragraphs.forEach(function (p) { p.node.textContent = p.words.join(' '); });
            resolve();
            return;
          }
          var pi = 0;
          var wi = 0;
          (function tick() {
            if (run !== self.run) return resolve();
            var p = paragraphs[pi];
            if (!p) return resolve();
            var step = 1 + Math.floor(Math.random() * 3);
            wi = Math.min(wi + step, p.words.length);
            p.node.textContent = p.words.slice(0, wi).join(' ');
            if (wi >= p.words.length) {
              pi++;
              wi = 0;
            }
            self.followReply(row);
            setTimeout(tick, 35 + Math.random() * 45);
          })();
        });

        return streamed.then(function () {
          if (run !== self.run) return;
          self.renderExtras(body, reply);
          self.thread.removeAttribute('aria-busy');
          self.followReply(row);
        });
      }

      renderExtras(body, reply) {
        if (reply.tips && reply.tips.length) {
          var ul = el('ul');
          reply.tips.forEach(function (tip) { ul.appendChild(el('li', null, tip)); });
          body.appendChild(ul);
        }

        if (reply.products && reply.products.length) {
          var recs = el('div', 'ai-recs');
          reply.products.forEach(function (p) {
            var card = el('div', 'ai-rec');
            var img = el('div', 'ai-rec-img');
            if (p.image) {
              var image = document.createElement('img');
              image.src = p.image;
              image.alt = '';
              image.loading = 'lazy';
              img.appendChild(image);
            }
            var info = el('div', 'ai-rec-info');
            var link = el('a', null, p.title);
            link.href = p.url;
            info.appendChild(link);
            info.appendChild(el('span', 'ai-rec-price', p.price));
            card.appendChild(img);
            card.appendChild(info);
            if (p.variantId) {
              var add = el('button', 'ai-btn', 'Add');
              add.type = 'button';
              add.setAttribute('data-ai-add', p.variantId);
              add.setAttribute('aria-label', 'Add ' + p.title + ' to basket');
              card.appendChild(add);
            } else {
              var view = el('a', 'ai-btn', 'View');
              view.href = p.url;
              card.appendChild(view);
            }
            recs.appendChild(card);
          });
          body.appendChild(recs);
        }

        if (reply.link === 'signin') {
          var signin = el('a', 'ai-btn ai-signin', 'Sign in to use Sleep AI — free');
          signin.href = this.loginUrl;
          body.appendChild(signin);
        }

        if (reply.link === 'cart') {
          var cart = el('a', 'ai-ghost ai-link', 'Go to basket →');
          cart.href = this.cartUrl;
          body.appendChild(cart);
        }

        if (reply.link === 'contact') {
          var contact = el('a', 'ai-ghost ai-link', 'Contact the team →');
          contact.href = this.contactUrl;
          body.appendChild(contact);
        }

        if (reply.followups && reply.followups.length) {
          var chips = el('div', 'ai-followups');
          reply.followups.forEach(function (f) {
            var chip = el('button', 'ai-chip', f);
            chip.type = 'button';
            chip.setAttribute('data-ai-send-text', f);
            chips.appendChild(chip);
          });
          body.appendChild(chips);
        }
      }

      addToCart(btn) {
        if (btn.disabled) return;
        var label = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';

        // Refresh theme: the cart is a drawer or a pop-up. Without updating them,
        // the item was added but the cart still looked empty until a page reload.
        var drawer = document.querySelector('cart-drawer');
        var notification = document.querySelector('cart-notification');
        var cartUi = drawer ? 'drawer' : notification ? 'notification' : 'page';
        var status;

        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(cartAddRequest(btn.getAttribute('data-ai-add'), cartUi, window.location.pathname))
        })
          .then(function (res) {
            status = res.status;
            if (!res.ok) throw new Error('add failed');
            return res.json();
          })
          .then(function (data) {
            btn.textContent = 'Added';
            if (drawer && typeof drawer.renderContents === 'function') {
              drawer.classList.remove('is-empty');
              drawer.renderContents(data);
            } else if (notification && typeof notification.renderContents === 'function') {
              notification.renderContents(data);
            } else {
              var bubble = document.getElementById('cart-icon-bubble');
              var html = data.sections && data.sections['cart-icon-bubble'];
              if (bubble && html) {
                var section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
                if (section) bubble.innerHTML = section.innerHTML;
              }
            }
          })
          .catch(function () {
            btn.textContent = cartAddErrorLabel(status);
          })
          .then(function () {
            setTimeout(function () {
              btn.disabled = false;
              btn.textContent = label;
            }, 1600);
          });
      }
    }

    customElements.define('delim-ai-chat', DelimAiChat);

    // "Ask DELIM Sleep AI about this" links outside the chat (the product page
    // buy box): ask in the chat on this page instead of following the link.
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('[data-ai-ask]');
      if (!link || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var chat = document.querySelector('delim-ai-chat');
      if (!chat || !chat._ready) return;
      e.preventDefault();
      chat.send(askFromQuery('?ask=' + encodeURIComponent(link.getAttribute('data-ai-ask') || '')));
      chat.scrollToChat();
    });
  }

  var api = {
    SAFETY: SAFETY,
    TOPICS: TOPICS,
    pickProducts: pickProducts,
    topicProducts: topicProducts,
    productsByTag: productsByTag,
    scriptedReply: scriptedReply,
    withDelay: withDelay,
    guestNudge: guestNudge,
    normalizeAi: normalizeAi,
    chooseRoute: chooseRoute,
    aiErrorReply: aiErrorReply,
    cartAddRequest: cartAddRequest,
    cartAddErrorLabel: cartAddErrorLabel,
    askFromQuery: askFromQuery,
    ASK_MAX: ASK_MAX,
    chatScrollTarget: chatScrollTarget
  };

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('delim-ai-chat')) {
    defineElement();
  }
})();
